import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { WifiOff } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Shell } from '@/components/layout/Shell';
import { Dashboard } from '@/pages/Dashboard';
import { Publishing } from '@/pages/Publishing';
import { Calendar } from '@/pages/Calendar';
import { Inbox } from '@/pages/Inbox';
import { AIStudio } from '@/pages/AIStudio';
import { Media } from '@/pages/Media';
import { Queues } from '@/pages/Queues';
import { BulkPosting } from '@/pages/BulkPosting';
import { Analytics } from '@/pages/Analytics';
import { CreatorResearch } from '@/pages/CreatorResearch';
import { Settings } from '@/pages/Settings';
import { Admin } from '@/pages/Admin';
import { InviteAccept } from '@/pages/InviteAccept';
import { PublicReport } from '@/pages/PublicReport';
import { SignIn } from '@/pages/auth/SignIn';
import { SignUp } from '@/pages/auth/SignUp';
import LandingPage from '@/pages/landing/LandingPage';
import PrivacyPolicy from '@/pages/legal/PrivacyPolicy';
import TermsOfService from '@/pages/legal/TermsOfService';

function LoadingScreen() {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
    </div>
  );
}

/**
 * The stored session couldn't be checked (API unreachable or erroring). The
 * token is kept, so offer a retry rather than bouncing to /sign-in.
 */
function AuthUnavailable() {
  const { retryAuth } = useAuth();
  return (
    <div className="flex h-screen items-center justify-center bg-background p-4">
      <EmptyState
        icon={WifiOff}
        title="Can't reach Enflux right now"
        body="You're still signed in. Check your connection and try again."
        action={<Button variant="primary" size="md" onClick={retryAuth}>Try again</Button>}
      />
    </div>
  );
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded, authError } = useAuth();

  if (!isLoaded) {
    return <LoadingScreen />;
  }

  if (authError) {
    return <AuthUnavailable />;
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  return <>{children}</>;
}

/**
 * The root URL serves double duty: the marketing landing for visitors, and the
 * dashboard for anyone already signed in. Without this, enflux.cc bounced
 * straight to /sign-in and the landing had nowhere to live.
 */
function RootRoute() {
  const { isSignedIn, isLoaded, authError } = useAuth();

  if (!isLoaded) {
    return <LoadingScreen />;
  }

  if (authError) {
    return <AuthUnavailable />;
  }

  if (!isSignedIn) {
    return <LandingPage />;
  }

  return (
    <Shell>
      <Dashboard />
    </Shell>
  );
}

/** Gate platform-admin routes behind the superadmin flag. */
function SuperadminGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user?.isSuperadmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Landing for visitors, dashboard once signed in. */}
        <Route path="/" element={<RootRoute />} />
        {/* The landing used to live here. Keep one canonical URL. */}
        <Route path="/landing" element={<Navigate to="/" replace />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/sign-up" element={<SignUp />} />
        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route path="/report/:token" element={<PublicReport />} />
        {/* Public legal pages — Google OAuth verification requires both. */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route
          path="/*"
          element={
            <AuthGuard>
              <Shell>
                <Routes>
                  <Route index element={<Dashboard />} />
                  <Route path="publishing" element={<Publishing />} />
                  <Route path="calendar" element={<Calendar />} />
                  <Route path="queues" element={<Queues />} />
                  <Route path="bulk-posting" element={<BulkPosting />} />
                  <Route path="inbox" element={<Inbox />} />
                  <Route path="ai-studio" element={<AIStudio />} />
                  <Route path="media" element={<Media />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="research" element={<CreatorResearch />} />
                  <Route path="settings/*" element={<Settings />} />
                  <Route
                    path="admin/*"
                    element={
                      <SuperadminGuard>
                        <Admin />
                      </SuperadminGuard>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Shell>
            </AuthGuard>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
